import {useCallback, useEffect, useMemo, useState} from 'react'
import {GetPaginatedResponse} from "../../../../types";
import {useAsync} from "react-async-hook";

export type ListParams = {
  readonly page: number
  readonly tab: string
  readonly searchTerm: string
  readonly sort: { readonly sortBy: string; readonly order: string } | null
}

export type ParamsUpdater<T> = <K extends keyof T>(key: K, value: T[K]) => void

export type ListStateHook<T, P extends ListParams> = {
  readonly params: P
  readonly onPageChange: (page: number) => void
  readonly setActiveTab: (tab: string) => void
  readonly toggleAll: () => void
  readonly patchListParams: ParamsUpdater<P>
  readonly setListParams: (newParams: P) => void
  readonly onChangeSearch: (term: string) => void
  readonly resetState: () => P
  readonly select: (item: T) => void
  readonly selectedItems: ReadonlyArray<T>
  readonly items: ReadonlyArray<T>
  readonly pageCount: number
  readonly refetch: () => void
  readonly loading: boolean
  readonly allSelected: boolean
  readonly commentCount: number
}

export type RequestParams<P> = Omit<P, 'page' | 'sort'> & { sortBy: string; order: string; page: number }

export const useListState = <T, P extends ListParams>(
  initState: P,
  query: (state: RequestParams<P>) => Promise<GetPaginatedResponse<T>>
): ListStateHook<T, P> => {
  const [listState, setListState] = useState(initState)
  const [selectedItems, setSelectedItems] = useState<ReadonlyArray<T>>([])

  const fetcher = useAsync<GetPaginatedResponse<T>>(() => {
    const {sort, page, ...rest} = listState;
    const params = {
      ...rest,
      sortBy: listState.sort?.sortBy,
      order: listState.sort?.order,
      page: page - 1
    }
    return query(params)
  }, [listState])

  const items = useMemo(() => {
    return fetcher.result?.rows || []
  }, [fetcher.result?.rows])

  const commentCount = useMemo(() => {
    return fetcher.result?.count || 0
  }, [fetcher.result?.count])

  const pageCount = useMemo(() => {
    return Math.ceil((fetcher.result?.count || 0)/20)
  }, [fetcher.result?.count])

  const refetch = useCallback(() => {
    void fetcher.execute()
  }, [fetcher.loading])

  const setListParams = useCallback((update: P | ((prev: P) => P)) => {
    setListState(update)
  }, [fetcher.loading])

  // Could've used Partial but Partial doesnt work well with generic types
  // https://github.com/microsoft/TypeScript/issues/13442
  const patchListParams = useCallback<ParamsUpdater<P>>((key, value) => {
    const newParamsList = {
      ...listState,
      [key]: value
    }
    setListParams(newParamsList)
  }, [fetcher.loading, listState])

  const resetState = useCallback(() => {
    setListParams(initState)
    return initState
  }, [fetcher.loading])

  const onPageChange = useCallback((page: number) => {
    setListParams((prev) => ({ ...prev, page }))
  }, [fetcher.loading])

  const setActiveTab = useCallback((tab: string) => {
    setListParams((prev) => ({ ...prev, tab }))
  }, [fetcher.loading])

  const onChangeSearch = useCallback(
    (term: string) => {
      patchListParams('searchTerm', term)
    },
    [patchListParams]
  )

  const allSelected = items.length ? (selectedItems.length === items.length) : false
  const toggleAll = useCallback((): void => {
    setSelectedItems(allSelected ? [] : items)
  }, [allSelected, items])

  const select = useCallback(
    (item: T): void => {
      setSelectedItems(
        selectedItems.includes(item)
          ? selectedItems.filter((i) => i !== item)
          : [...selectedItems, item]
      )
    },
    [selectedItems]
  )

  useEffect(() => {
    setSelectedItems([])
  }, [items])

  return {
    params: listState,
    onPageChange,
    setActiveTab,
    select,
    allSelected,
    toggleAll,
    onChangeSearch,
    selectedItems,
    patchListParams,
    setListParams,
    items,
    pageCount,
    resetState,
    refetch,
    loading: fetcher.loading,
    commentCount
  }
}
